'use client';

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { DLG_BTN_SECONDARY } from '@/components/ui/dialogButtons';

interface Horario {
  hora_inicio: string;
  hora_fin: string;
}

interface TemporadaInfo {
  nombre: string;
  desde: string;
  hasta: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  horarios: Record<string, Horario[]>;
  horariosTemporada?: Record<string, Horario[]>;
  temporada?: TemporadaInfo | null;
  diasSemana: string[];
}

const WorkerHorarioDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  horarios,
  horariosTemporada = {},
  temporada = null,
  diasSemana,
}) => {
  const rejilla = (fuente: Record<string, Horario[]>) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {diasSemana.map((dia) => {
        const franjas = fuente[dia];
        if (!franjas || franjas.length === 0) return null;

        return (
          <div
            key={dia}
            className="border border-gray-200 dark:border-white/10 rounded-lg bg-gray-50 dark:bg-white/5 p-3"
          >
            <label className="block font-semibold text-gray-700 dark:text-gray-300 capitalize mb-2">
              {dia}
            </label>

            {franjas.map((f, i) => (
              <div
                key={i}
                className="px-3 py-2 mb-1 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-gray-200 shadow-sm"
              >
                {f.hora_inicio} – {f.hora_fin}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              Horario semanal
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5 space-y-4">
          {temporada && (
            <div className="rounded-xl border border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-4">
              <p className="font-semibold text-emerald-900 dark:text-emerald-300">
                {temporada.nombre}
              </p>
              <p className="text-sm text-emerald-800 dark:text-emerald-400 mb-3">
                Del {temporada.desde} al {temporada.hasta} haces esta jornada.
                Fuera de esas fechas, tu horario general.
              </p>
              {rejilla(horariosTemporada)}
            </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
            {temporada && (
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Horario general
              </p>
            )}
            {rejilla(horarios)}
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={`w-full ${DLG_BTN_SECONDARY}`}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkerHorarioDialog;